import { cn } from '@/lib/utils/utils';
import { BIBLE_BOOKS, type BibleBook } from '@/lib/utils/bible-utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { SECTION_LABELS } from '../passage-selector-content';
import { useMemo } from 'react';

interface CanonRailProps {
    currentBook?: BibleBook;
    isDarkMode?: boolean;
    onSelect?: (book: BibleBook) => void;
    bookVerseTotals?: Partial<Record<BibleBook, number>>;
}

/**
 * CanonRail: thin vertical rail showing all books of the Bible.
 * Uses number of chapters as a proxy for height weighting.
 */
export function CanonRail({ currentBook, isDarkMode, onSelect, bookVerseTotals }: CanonRailProps) {
    const TOTAL_CHAPTERS = useMemo(() => BIBLE_BOOKS.reduce((sum, b) => sum + b.numChapters, 0), []);
    const totalVersesProvided = useMemo(() => bookVerseTotals ? Object.values(bookVerseTotals).reduce((s, v) => s + (v || 0), 0) : 0, [bookVerseTotals]);
    const gridTemplateRows = useMemo(() => {
        const rows = BIBLE_BOOKS.map(b => {
            const verses = bookVerseTotals?.[b.name] ?? 0;
            if (verses > 0 && totalVersesProvided > 0) return `${(verses / totalVersesProvided) * 100}%`;
            return `${(b.numChapters / TOTAL_CHAPTERS) * 100}%`;
        }).join(' ');
        return rows;
    }, [bookVerseTotals, totalVersesProvided, TOTAL_CHAPTERS]);

    return (
        <div
            className={cn(
                'w-4 md:w-6 h-full rounded-xl overflow-hidden border',
                isDarkMode ? 'border-white/20 bg-white/10' : 'border-white/20 bg-white/10'
            )}
            aria-label="Bible books rail"
        >
            <div className="grid w-full h-full" style={{ gridTemplateRows }}>
                {BIBLE_BOOKS.map((book, index) => {
                    const isCurrent = book.name === currentBook;
                    return (
                        <Tooltip key={book.name} delayDuration={250}>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    aria-label={`Select ${book.name}`}
                                    className={cn(
                                        'cursor-pointer relative w-full h-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
                                        isCurrent
                                            ? 'bg-yellow-400/80 dark:bg-yellow-500/80'
                                            : 'bg-white/25 dark:bg-white/20 hover:bg-white/35 dark:hover:bg-white/30'
                                    )}
                                    onClick={() => onSelect?.(book.name)}
                                >
                                    {index < BIBLE_BOOKS.length - 1 && (
                                        <div className="absolute bottom-0 left-0 right-0 h-px bg-white/12" />
                                    )}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                                <div className="text-xs">
                                    <div className="font-semibold">{book.name}</div>
                                    <div className="opacity-80">{SECTION_LABELS[book.section]}</div>
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    );
                })}
            </div>
        </div>
    );
}



